import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import getVideoId from "get-video-id";
import moment from "moment";
import { Col, Container, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { TeacherContext } from "../../context/TeacherContext";
import styled from "styled-components";
import { SButton, SContainer, SError, SFormControl } from "../../components/BasicComponents";
import AppNavbar from "../../components/Navbars/AppNavbar";
import YouTubePlayer from "../../components/YouTubePlayer/YouTubePlayer";
import { toastError } from "../../utils/Toast";
import { isLectureBegun } from "../../utils/lectureUtils";
import Loading from "../../components/Loading/Loading";
import { te } from "../../utils/errorsTranslation";

const UpdateLecturePage = (props) => {
  const { t } = useTranslation("", { keyPrefix: "lecture.update" });

  const { getLectureInfo, updateFlippedLecture, updateLiveLecture, uploadSlides, loading, error, resetError } =
    useContext(TeacherContext);
  const [lecture, setLecture] = useState(null);
  const [name, setName] = useState("");
  const [fileData, setFileData] = useState(null);
  const [startTime, setStartTime] = useState(moment().format("YYYY-MM-DDTHH:mm"));
  const [endTime, setEndTime] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [responseError, setResponseError] = useState("");

  const history = useHistory();

  useEffect(() => {
    if (props.match.path !== "/update-lecture/:lectureUUID") {
      history.push("/error");
    }

    const lectureUUID = props.match.params.lectureUUID;
    getLectureInfo(lectureUUID).then((lecture) => {
      if (lecture) {
        setLecture(lecture);
        setName(lecture.name);
        setVideoUrl("https://youtu.be/" + lecture.videoId);
        if (lecture.type === "live") {
          setStartTime(lecture.startTime);
          setEndTime(lecture.endTime);
        }
      } else {
        toastError(t("error"));
        history.push("/teacher");
      }
    });

    if (error) setResponseError(error);

    return () => resetError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdate = (e) => {
    e.preventDefault();
    //Get video id
    const videoId = getVideoId(videoUrl).id;
    //Send the request to create a new lecture
    if (lecture.type === "flipped") {
      updateFlippedLecture(lecture.courseUUID, { lectureUUID: lecture.uuid, name, videoId }).then(async (lecture) => {
        if (lecture) {
          if (fileData) {
            await uploadSlides({ slides: fileData, lectureUUID: lecture.uuid });
          }

          history.push("/teacher");
        }
      });
    } else if (lecture.type === "live") {
      updateLiveLecture(lecture.courseUUID, { lectureUUID: lecture.uuid, name, videoId, startTime, endTime }).then(
        async (lecture) => {
          if (lecture) {
            if (fileData) {
              await uploadSlides({ slides: fileData, lectureUUID: lecture.uuid });
            }

            history.push("/teacher");
          }
        }
      );
    }
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <SContainer className="d-flex flex-column" fluid fill="true" dark="true">
          <Row>
            <AppNavbar dark="true" />
          </Row>

          <Styles>
            <Form onSubmit={onUpdate}>
              <Container className="mt-5 h-100">
                <Row>
                  <Col lg={6} sm={12} className="pe-5">
                    <h1 className="fw-bolder ms-2">{t("title")}</h1>
                    <SFormControl
                      required
                      className="fs-4 my-4"
                      type="text"
                      placeholder="Title"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                    <SFormControl
                      className="fs-4 my-4"
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => !isLectureBegun(lecture) && setFileData(e.target.files[0])}
                      disabled={isLectureBegun(lecture)}
                    />
                    {lecture?.type === "live" && (
                      <>
                        <SFormControl
                          className="fs-4 my-4"
                          required
                          type="datetime-local"
                          min={startTime}
                          value={moment(startTime).format("YYYY-MM-DDTHH:mm")}
                          onChange={(e) =>
                            !isLectureBegun(lecture) && setStartTime(moment(e.target.value).format("YYYY-MM-DDTHH:mm"))
                          }
                          disabled={isLectureBegun(lecture)}
                        />
                        <SFormControl
                          required
                          className="fs-4 my-4"
                          type="datetime-local"
                          min={startTime}
                          value={moment(endTime).format("YYYY-MM-DDTHH:mm")}
                          onChange={(e) =>
                            !isLectureBegun(lecture) && setEndTime(moment(e.target.value).format("YYYY-MM-DDTHH:mm"))
                          }
                          disabled={isLectureBegun(lecture)}
                        />
                      </>
                    )}
                    <SFormControl
                      className="fs-4 my-4"
                      type="text"
                      placeholder={t("link")}
                      value={`https://youtu.be/${videoUrl}`}
                      onChange={(e) => !isLectureBegun(lecture) && setVideoUrl(e.target.value)}
                      disabled={isLectureBegun(lecture)}
                    />
                  </Col>
                  <Col lg={6} sm={12} className="d-flex flex-column flex-grow-1 justify-content-center">
                    <SVideoPlayer className="mb-4">
                      <YouTubePlayer videoId={getVideoId(videoUrl).id} />
                    </SVideoPlayer>
                    <SButton type="submit" dark="true" className="mt-4 w-100 fs-5">
                      {t("submit")}
                    </SButton>
                  </Col>
                </Row>
                <Row>{responseError && <SError>{te(responseError.message)}</SError>}</Row>
              </Container>
            </Form>
          </Styles>
        </SContainer>
      )}
    </>
  );
};

const Styles = styled.div`
  flex-grow: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;

  .container {
    height: 100%;

    color: var(--text-primary-dark) !important;
    .col {
      height: 100%;
    }
  }
`;

const SVideoPlayer = styled.div`
  position: relative;

  border-radius: 2em;
  border: none;
  box-shadow: none;

  padding: 0.5em 1.5em;
  width: 100%;
  height: 20rem;

  overflow: hidden;

  background-color: var(--secondary-dark);
  color: var(--text-primary-dark);

  ::placeholder {
    text-transform: uppercase;
    color: var(--text-secondary-dark);
  }

  :focus {
    box-shadow: none;
    background-color: var(--secondary-dark);
    color: var(--text-primary-dark);
  }

  iframe {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 100%;
    width: 100%;
    transform: translate(-50%, -50%);
  }
`;

export default UpdateLecturePage;
